import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Upload, Shield, FileCheck, Loader2, RefreshCw, CreditCard, FileText, PenTool, Clock, Lock, CalendarIcon, User, Mail, Eye, EyeOff, LogIn, AlertTriangle, Eraser, Package, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Calendar } from './ui/calendar';
import { packs, getProtectPlus, PROTECT_PLUS_FRANCHISE } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import ContractDisplay from './ContractDisplay';
import SignatureCanvas from 'react-signature-canvas';
import PaymentMethodSelector from './payment/PaymentMethodSelector';
import ProtectPlusSelector from './payment/ProtectPlusSelector';
import SEPAPaymentForm from './payment/SEPAPaymentForm';
import { trackProgress, markCompleted } from '../lib/reservationTracker';
import { fr } from 'date-fns/locale';
import { format, addDays, differenceInDays } from 'date-fns';

const LOGO_WHITE = process.env.REACT_APP_LOGO_URL;
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// ── TEMPORAIRE : rupture de stock ──────────────────────────────────────────
// Bloque (grise) toutes les dates de réservation jusqu'au 18 juin 2026 INCLUS.
// Première date réservable = 19 juin 2026.
// Pour lever le blocage quand le stock revient : mettre TEMP_BLOCK_UNTIL = null.
const TEMP_BLOCK_UNTIL = new Date(2026, 5, 18); // mois 0-indexé : 5 = juin

// Date minimale réservable = max(J+5, lendemain de la période bloquée)
const getMinBookingDate = () => {
  const jPlus5 = addDays(new Date(), 5);
  jPlus5.setHours(0, 0, 0, 0);
  if (TEMP_BLOCK_UNTIL) {
    const firstAvailable = addDays(TEMP_BLOCK_UNTIL, 1);
    firstAvailable.setHours(0, 0, 0, 0);
    if (firstAvailable > jPlus5) return firstAvailable;
  }
  return jPlus5;
};

const ReservationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPack, setSelectedPack] = useState(() => {
    if (location.state?.selectedPack) return location.state.selectedPack;
    try {
      const saved = sessionStorage.getItem('phocea_reservation_pack');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [rcFile, setRcFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRedirecting, setPaymentRedirecting] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  // Signing state
  const [pandadocDocId, setPandadocDocId] = useState(null);
  const [pandadocSessionId, setPandadocSessionId] = useState(null);
  const [pandadocSigned, setPandadocSigned] = useState(false);
  const [pandadocLoading, setPandadocLoading] = useState(false);
  const [signatureData, setSignatureData] = useState(null); // base64 PNG of canvas signature
  const [availability, setAvailability] = useState({});
  const sigCanvasRef = useRef(null);
  const sigContainerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(500);
  const [showContract, setShowContract] = useState(false);
  // Payment method ("card" | "sepa") + SEPA flow state
  const [paymentMethod, setPaymentMethod] = useState('card');
  // Protect+ optional coverage
  const [protectPlus, setProtectPlus] = useState(false);
  const [protectAccepted, setProtectAccepted] = useState(false);
  // Collapsible "return info" disclosure on step 1
  const [showReturnInfo, setShowReturnInfo] = useState(false);
  const [sepaClientSecret, setSepaClientSecret] = useState(null);
  const [sepaCheckout, setSepaCheckout] = useState(null); // {request_id, reservation_id, first_charge_amount}
  const [sepaFormOpen, setSepaFormOpen] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Auth form state
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  
  // Date selection state
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    const defaults = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      siren: '',
      activityType: '',
      address: '',
      city: '',
      postalCode: ''
    };
    try {
      const saved = sessionStorage.getItem('phocea_reservation_form');
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch { return defaults; }
  });

  // Manual order pre-fill state
  const [manualOrderId, setManualOrderId] = useState(null);
  const [isManualOrder, setIsManualOrder] = useState(false);
  const [manualOrderLoading, setManualOrderLoading] = useState(false);
  const [manualOrderPricing, setManualOrderPricing] = useState(null); // { finalPrice, deposit, discountValue, discountType }

  const steps = [
    { id: 1, title: 'Choix du pack', icon: 'Package' },
    { id: 2, title: 'Connexion', icon: 'User' },
    { id: 3, title: 'Vos informations', icon: 'FileText' },
    { id: 4, title: 'Contrat & Signature', icon: 'PenTool' },
    { id: 5, title: 'Paiement', icon: 'CreditCard' },
    { id: 6, title: 'Confirmation', icon: 'Check' }
  ];

  // Map pack IDs to backend pack_ids
  const packIdMap = {
    1: 'pack_dj_booth',
    2: 'pack_compact',
    3: 'pack_standard',
    4: 'pack_xl'
  };

  // Resize signature canvas to match container width
  useEffect(() => {
    const updateCanvasWidth = () => {
      if (sigContainerRef.current) {
        setCanvasWidth(sigContainerRef.current.offsetWidth);
      }
    };
    updateCanvasWidth();
    window.addEventListener('resize', updateCanvasWidth);
    return () => window.removeEventListener('resize', updateCanvasWidth);
  }, [currentStep, contractAccepted]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    if (sessionId && paymentStatus === 'success') {
      setCurrentStep(5);
      setIsProcessing(true);
      toast.info('Vérification du paiement en cours...');
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Paiement annulé.');
      setCurrentStep(4);
    }
  }, [searchParams]);

  // Check if user is already logged in
  useEffect(() => {
    checkExistingUser();
  }, []);

  // Persist selectedPack to sessionStorage (survives Stripe back-navigation & refresh)
  useEffect(() => {
    if (selectedPack) {
      try { sessionStorage.setItem('phocea_reservation_pack', JSON.stringify(selectedPack)); } catch {}
    }
  }, [selectedPack]);

  // Persist formData to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem('phocea_reservation_form', JSON.stringify(formData)); } catch {}
  }, [formData]);

  // Clear persisted reservation state once payment is confirmed (step 6)
  useEffect(() => {
    if (currentStep === 6) {
      try {
        sessionStorage.removeItem('phocea_reservation_pack');
        sessionStorage.removeItem('phocea_reservation_form');
      } catch {}
    }
  }, [currentStep]);

  // Fetch pack availability
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${API}/packs/availability`);
        setAvailability(res.data);
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      }
    };
    fetchAvailability();
  }, []);

  // Check for manual order pre-fill link
  useEffect(() => {
    const moId = searchParams.get('manual_order');
    const moToken = searchParams.get('token');
    if (moId && moToken) {
      loadManualOrder(moId, moToken);
    }
  }, [searchParams]);

  // Funnel heartbeat — every step change + meaningful form update is reported
  // to the backend so admins can see abandoned reservations.
  useEffect(() => {
    if (currentStep < 1) return;
    trackProgress({
      current_step: currentStep,
      pack_id: selectedPack?.id || selectedPack?.pack_id || '',
      pack_name: selectedPack?.name || '',
      pack_surface: selectedPack?.surface || '',
      rental_start_date: formData.startDate || '',
      rental_end_date: formData.endDate || '',
      monthly_price: effectiveMonthlyPrice ?? selectedPack?.price ?? null,
      caution: effectiveDeposit ?? selectedPack?.caution ?? null,
      transport_fees: transportFees || 0,
      pandadoc_signed: Boolean(pandadocSigned),
      signature_drawn: Boolean(signatureData),
      payment_method_selected: paymentMethod || null,
      customer: (formData.firstName || formData.email) ? {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        activityType: formData.activityType,
        siren: formData.siren,
      } : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    selectedPack?.id,
    formData.startDate,
    formData.endDate,
    formData.email,
    formData.firstName,
    formData.lastName,
    formData.phone,
    formData.company,
    pandadocSigned,
    signatureData,
    paymentMethod,
  ]);

  const loadManualOrder = async (orderId, token) => {
    setManualOrderLoading(true);
    try {
      const response = await axios.get(`${API}/manual-order-prefill/${orderId}?token=${token}`);
      const data = response.data;
      
      // Set pack (catalogue OR custom — synthesize a pack object so the UI works seamlessly)
      const packMatch = packs.find(p => p.id === data.pack_frontend_id);
      if (packMatch && !data.is_custom_pack) {
        setSelectedPack(packMatch);
      } else if (data.pack_name) {
        setSelectedPack({
          id: data.pack_id || 'custom',
          pack_id: data.pack_id || 'custom',
          name: data.pack_name,
          surface: data.pack_surface || '',
          price: data.final_price || 0,
          caution: data.pack_deposit || 0,
          description: 'Configuration personnalisée par le commercial',
          is_custom_pack: true,
        });
      }
      
      // Set form data
      setFormData({
        firstName: data.customer.firstName || '',
        lastName: data.customer.lastName || '',
        email: data.customer.email || '',
        phone: data.customer.phone || '',
        company: data.customer.company || '',
        siren: data.customer.siren || '',
        activityType: data.customer.activityType || '',
        address: data.customer.address || '',
        city: data.customer.city || '',
        postalCode: data.customer.postalCode || ''
      });
      
      // Set rental date if available
      if (data.rental_start_date) {
        setDateRange({ from: new Date(data.rental_start_date), to: null });
      }
      
      setManualOrderId(orderId);
      setIsManualOrder(true);
      
      // Store manual order pricing (may include discount + transport fees)
      if (data.final_price) {
        setManualOrderPricing({
          finalPrice: data.final_price,
          deposit: data.pack_deposit,
          discountValue: data.discount_value || 0,
          discountType: data.discount_type || null,
          transportFees: Number(data.transport_fees || 0),
          pitch: data.pitch || '',
        });
      }
      
      // Check if user is already logged in by calling /auth/me directly
      // (isLoggedIn state might not be set yet due to parallel useEffect)
      let userLoggedIn = isLoggedIn;
      if (!userLoggedIn) {
        try {
          const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (meRes.data && meRes.data.email) {
            setGoogleUser(meRes.data);
            setIsLoggedIn(true);
            userLoggedIn = true;
            // Don't override manual order form data with Google user data
          }
        } catch { /* not logged in */ }
      }
      
      // Manual orders : on dirige toujours vers l'étape 3 (Informations)
      // pour que le client puisse vérifier ses infos, uploader sa RC pro
      // et voir la remise appliquée par le commercial.
      if (userLoggedIn) {
        setCurrentStep(3);
      } else {
        setCurrentStep(2);
      }
      
      toast.success('Commande pré-remplie par votre commercial. Finalisez votre réservation !');
    } catch (error) {
      console.error('Error loading manual order:', error);
      toast.error(error.response?.data?.detail || 'Lien de commande invalide ou expiré');
    } finally {
      setManualOrderLoading(false);
    }
  };

  const checkExistingUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data && response.data.email) {
        const user = response.data;
        setGoogleUser(user);
        setIsGoogleUser(true);
        setIsLoggedIn(true);
        
        // Fetch full profile data
        try {
          const profileRes = await axios.get(`${API}/auth/profile`, { withCredentials: true });
          const profile = profileRes.data;
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: profile.first_name || nameParts[0] || prev.firstName,
            lastName: profile.last_name || nameParts.slice(1).join(' ') || prev.lastName,
            email: user.email || prev.email,
            phone: profile.phone || prev.phone,
            company: profile.company || prev.company,
            siren: profile.siren || prev.siren,
            activityType: profile.activity_type || prev.activityType,
            address: profile.delivery_address || prev.address,
            city: profile.delivery_city || prev.city,
            postalCode: profile.delivery_postal_code || prev.postalCode,
          }));
        } catch {
          // Fallback to basic user data
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: nameParts[0] || prev.firstName,
            lastName: nameParts.slice(1).join(' ') || prev.lastName,
            email: user.email || prev.email
          }));
        }
        
        // Restore selected pack from localStorage (after Google auth redirect)
        const savedPack = localStorage.getItem('reservation_selected_pack');
        if (savedPack) {
          const pack = JSON.parse(savedPack);
          setSelectedPack(pack);
          localStorage.removeItem('reservation_selected_pack');
          setCurrentStep(3);
          toast.success('Connexion réussie ! Vos informations ont été pré-remplies.');
        }
      }
    } catch (error) {
      // User not logged in, that's fine
      setIsLoggedIn(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data && response.data.email) {
        const user = response.data;
        setGoogleUser(user);
        setIsGoogleUser(true);
        setIsLoggedIn(true);
        
        // Fetch full profile data
        try {
          const profileRes = await axios.get(`${API}/auth/profile`, { withCredentials: true });
          const profile = profileRes.data;
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: profile.first_name || nameParts[0] || '',
            lastName: profile.last_name || nameParts.slice(1).join(' ') || '',
            email: user.email || '',
            phone: profile.phone || prev.phone,
            company: profile.company || prev.company,
            siren: profile.siren || prev.siren,
            activityType: profile.activity_type || prev.activityType,
            address: profile.delivery_address || prev.address,
            city: profile.delivery_city || prev.city,
            postalCode: profile.delivery_postal_code || prev.postalCode,
          }));
        } catch {
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: user.email || ''
          }));
        }
        
        // Restore selected pack from localStorage
        const savedPack = localStorage.getItem('reservation_selected_pack');
        if (savedPack) {
          const pack = JSON.parse(savedPack);
          setSelectedPack(pack);
          localStorage.removeItem('reservation_selected_pack');
        }
        
        // Restore manual order context if returning from Google auth
        const savedMoId = localStorage.getItem('reservation_manual_order_id');
        const savedMoToken = localStorage.getItem('reservation_manual_order_token');
        if (savedMoId && savedMoToken) {
          localStorage.removeItem('reservation_manual_order_id');
          localStorage.removeItem('reservation_manual_order_token');
          await loadManualOrder(savedMoId, savedMoToken);
          window.history.replaceState({}, '', '/reservation');
          return;
        }
        
        // Skip step 2 (auth) and go directly to step 3 (Informations)
        setCurrentStep(3);
        toast.success('Connexion réussie ! Vos informations ont été pré-remplies.');
        
        // Clean URL
        window.history.replaceState({}, '', '/reservation');
      }
    } catch (error) {
      toast.error('Erreur lors de la récupération des informations');
    }
  };

  const handleGoogleLogin = () => {
    // Save current pack selection
    if (selectedPack) {
      localStorage.setItem('reservation_selected_pack', JSON.stringify(selectedPack));
    }
    // Save manual order context for restoration after Google auth redirect
    if (isManualOrder && manualOrderId) {
      const moToken = new URLSearchParams(window.location.search).get('token');
      localStorage.setItem('reservation_manual_order_id', manualOrderId);
      if (moToken) localStorage.setItem('reservation_manual_order_token', moToken);
    }
    // Save flag to redirect back to reservation after auth
    localStorage.setItem('auth_redirect', '/reservation');
    // Redirect to Google auth via standard callback
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Email authentication handlers
  const handleAuthInputChange = (e) => {
    setAuthData({ ...authData, [e.target.name]: e.target.value });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!authData.email || !authData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: authData.email,
        password: authData.password
      }, { withCredentials: true });
      
      const user = response.data;
      setGoogleUser(user);
      setIsLoggedIn(true);
      
      // Fetch full profile and pre-fill all fields
      if (!isManualOrder) {
        try {
          const profileRes = await axios.get(`${API}/auth/profile`, { withCredentials: true });
          const profile = profileRes.data;
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: profile.first_name || nameParts[0] || '',
            lastName: profile.last_name || nameParts.slice(1).join(' ') || '',
            email: user.email || '',
            phone: profile.phone || prev.phone,
            company: profile.company || prev.company,
            siren: profile.siren || prev.siren,
            activityType: profile.activity_type || prev.activityType,
            address: profile.delivery_address || prev.address,
            city: profile.delivery_city || prev.city,
            postalCode: profile.delivery_postal_code || prev.postalCode,
          }));
        } catch {
          const nameParts = (user.name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: user.email || ''
          }));
        }
      }
      
      toast.success('Connexion réussie !');
      setCurrentStep(3);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    if (!authData.email || !authData.password || !authData.name) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (authData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (authData.password !== authData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: authData.email,
        password: authData.password,
        name: authData.name
      }, { withCredentials: true });
      
      const user = response.data;
      setGoogleUser(user);
      setIsLoggedIn(true);
      
      // Pre-fill form with registration data
      if (!isManualOrder) {
        const nameParts = (user.name || '').split(' ');
        setFormData(prev => ({
          ...prev,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          email: user.email || ''
        }));
      }
      
      toast.success('Compte créé avec succès !');
      if (typeof window.fbq === 'function') window.fbq('track', 'CompleteRegistration');
      setCurrentStep(3);
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setAuthLoading(false);
    }
  };

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 10MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result || '').toString().split(',')[1] || '';
      setRcFile({
        name: file.name,
        size: file.size,
        type: file.type,
        base64,
      });
      toast.success('Document RC téléchargé avec succès');
    };
    reader.onerror = () => toast.error("Impossible de lire le fichier");
    reader.readAsDataURL(file);
  };

  const validateStep1 = () => {
    if (!selectedPack) {
      toast.error('Veuillez sélectionner un pack');
      return false;
    }
    if (!dateRange.from) {
      toast.error('Veuillez sélectionner une date de début');
      return false;
    }
    if (protectActive && !protectAccepted) {
      toast.error("Veuillez accepter les conditions de l'avenant Protect + pour continuer");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Step 2 is login - if user is already logged in, it's valid
    if (isLoggedIn) {
      return true;
    }
    toast.error('Veuillez vous connecter ou créer un compte');
    return false;
  };

  const validateStep3 = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'company', 'siren', 'activityType', 'address', 'city', 'postalCode'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return false;
      }
    }
    if (!rcFile && !protectActive) {
      toast.error('Veuillez télécharger votre attestation RC');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Veuillez entrer un email valide');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!contractAccepted) {
      toast.error('Veuillez accepter les conditions générales');
      return false;
    }
    if (!pandadocSigned && !signatureData) {
      toast.error('Veuillez signer le contrat avant de procéder au paiement');
      return false;
    }
    return true;
  };

  // Clear canvas signature (fallback)
  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setSignatureData(null);
    }
  };

  // Save canvas signature + upload to PandaDoc in background
  const saveSignature = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      const dataUrl = sigCanvasRef.current.toDataURL('image/png');
      setSignatureData(dataUrl);
      toast.success('Signature enregistrée ! Vous pouvez procéder au paiement.');
      // Upload to PandaDoc in background for certification
      uploadToPandaDoc(dataUrl);
    } else {
      toast.error('Veuillez dessiner votre signature');
    }
  };

  // Background PandaDoc upload for certification
  const uploadToPandaDoc = async (signatureBase64) => {
    setPandadocLoading(true);
    try {
      const reservationData = {
        id: manualOrderId || `temp_${Date.now()}`,
        pack_name: selectedPack?.name || '',
        pack_id: selectedPack?.id || '',
        pitch: manualOrderPricing?.pitch || '',
        monthly_price: effectiveMonthlyPrice,
        caution: effectiveDeposit,
        customer: formData,
        rental_start_date: dateRange.from ? dateRange.from.toISOString() : null,
        created_at: new Date().toISOString(),
        cgv_accepted: true,
        cgv_accepted_at: new Date().toISOString(),
        source: isManualOrder ? 'manual_order' : 'direct',
        client_signature_base64: signatureBase64
      };

      const response = await axios.post(`${API}/pandadoc/upload-and-sign`, {
        reservation_data: reservationData,
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email
        }
      });

      if (response.data.success) {
        setPandadocDocId(response.data.document_id);
        setPandadocSessionId(response.data.session_id);
        setPandadocSigned(true);
        toast.success('Contrat certifié par PandaDoc !');
      }
    } catch (error) {
      console.error('PandaDoc background upload error:', error);
      // Signature canvas is already saved — proceed without PandaDoc
    } finally {
      setPandadocLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 1 && isLoggedIn) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentStep === 2) {
      if (isLoggedIn) {
        setCurrentStep(3);
        return;
      }
      if (!validateStep2()) return;
    }
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep === 4) {
      if (!validateStep4()) return;
      if (paymentMethod === 'sepa') {
        initiateSEPAPayment();
      } else {
        initiateStripePayment();
      }
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 6));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const initiateStripePayment = async () => {
    try {
      setAuthLoading(true);
      setPaymentRedirecting(true);

      // Single combined call: create reservation + Stripe session in one round-trip
      const payload = {
        pack_id: selectedPack?.id || selectedPack?.pack_id,
        pack_name: selectedPack?.name,
        pack_surface: selectedPack?.surface || '',
        protect_plus: protectActive,
        customer: {
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          company: formData.company,
          siren: formData.siren,
          activityType: formData.activityType,
        },
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`.trim(),
        rental_start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : (formData.startDate || null),
        rental_end_date: formData.endDate,
        delivery_address: formData.address,
        delivery_city: formData.city,
        delivery_postal_code: formData.postalCode,
        monthly_price: effectiveMonthlyPrice || selectedPack?.price,
        caution: effectiveDeposit || selectedPack?.caution || selectedPack?.caution_total || 0,
        transport_fees: transportFees,
        pitch: manualOrderPricing?.pitch || '',
        manual_order_id: isManualOrder ? searchParams.get('manual_order') : null,
        client_token: isManualOrder ? searchParams.get('token') : null,
        rc_pro: rcFile ? {
          filename: rcFile.name,
          content_type: rcFile.type,
          size: rcFile.size,
          base64: rcFile.base64,
        } : null,
        origin_url: window.location.origin,
      };

      const response = await axios.post(`${API}/checkout/create`, payload, { withCredentials: true });

      if (response.data.url) {
        if (typeof window.fbq === 'function') window.fbq('track', 'InitiateCheckout', {content_name: selectedPack?.name, value: effectiveMonthlyPrice, currency: 'EUR'});
        import('../lib/analytics').then(m => m.trackEvent('reservation_started', { pack: selectedPack?.name }));
        markCompleted(); // funnel completed (about to redirect to Stripe)
        // Keep overlay visible until browser actually navigates to Stripe
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setPaymentRedirecting(false);
    } finally {
      setAuthLoading(false);
    }
  };

  // SEPA Direct Debit flow — open modal immediately with spinner, then create
  // the SetupIntent in background. After mandate signature, redirect to /sepa/pending.
  const initiateSEPAPayment = async () => {
    // Open modal INSTANTLY — the form will render once client_secret is ready.
    setSepaClientSecret(null);
    setSepaCheckout(null);
    setSepaFormOpen(true);
    // Warmup Stripe.js in parallel (no-op if already loaded).
    import('../lib/stripe').then(m => m.getStripe());

    try {
      const payload = {
        pack_id: selectedPack?.id || selectedPack?.pack_id,
        pack_name: selectedPack?.name,
        pack_surface: selectedPack?.surface || '',
        protect_plus: protectActive,
        customer: {
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          company: formData.company,
          siren: formData.siren,
          activityType: formData.activityType,
        },
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`.trim(),
        rental_start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : (formData.startDate || null),
        rental_end_date: formData.endDate,
        delivery_address: formData.address,
        delivery_city: formData.city,
        delivery_postal_code: formData.postalCode,
        monthly_price: effectiveMonthlyPrice || selectedPack?.price,
        caution: effectiveDeposit || selectedPack?.caution || selectedPack?.caution_total || 0,
        transport_fees: transportFees,
        pitch: manualOrderPricing?.pitch || '',
        manual_order_id: isManualOrder ? searchParams.get('manual_order') : null,
        client_token: isManualOrder ? searchParams.get('token') : null,
        rc_pro: rcFile ? {
          filename: rcFile.name,
          content_type: rcFile.type,
          size: rcFile.size,
          base64: rcFile.base64,
        } : null,
        client_signature_base64: signatureData || null,
      };

      const response = await axios.post(`${API}/sepa/checkout/create`, payload, { withCredentials: true });
      setSepaClientSecret(response.data.client_secret);
      setSepaCheckout(response.data);
      if (typeof window.fbq === 'function') window.fbq('track', 'InitiateCheckout', {content_name: selectedPack?.name, value: response.data.first_charge_amount, currency: 'EUR'});
    } catch (error) {
      console.error('SEPA setup error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la demande SEPA');
      setSepaFormOpen(false);
    }
  };

  const onSEPAMandateSigned = (setupIntent) => {
    toast.success('Mandat signé. Demande transmise à notre équipe.');
    markCompleted(); // SEPA mandate signed → funnel completed
    // Record the reused contract signature + generate the official mandate PDF
    // (best-effort, must not block the redirect).
    if (sepaCheckout?.request_id) {
      axios.post(`${API}/sepa/checkout/mandate-signed`, {
        request_id: sepaCheckout.request_id,
        signature_base64: signatureData || '',
        setup_intent_id: setupIntent?.id || sepaCheckout?.setup_intent_id || null,
      }, { withCredentials: true }).catch((e) => {
        console.error('mandate-signed record failed:', e);
      });
      navigate(`/sepa/pending/${sepaCheckout.request_id}`);
    }
  };

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      toast.error('Vérification du paiement expirée. Vérifiez votre email.');
      return;
    }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      if (response.data.payment_status === 'paid') {
        setCurrentStep(6);
        toast.success('Paiement et abonnement confirmés !');
        if (typeof window.fbq === 'function') window.fbq('track', 'Purchase', {content_name: selectedPack?.name, value: effectiveMonthlyPrice, currency: 'EUR'});
        import('../lib/analytics').then(m => m.trackEvent('reservation_completed', { pack: selectedPack?.name, value: effectiveMonthlyPrice }));
        setPaymentDetails(response.data);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Session de paiement expirée.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Status check error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const prevStep = () => {
    // For manual orders, going back from step 4 returns to step 3 (Informations)
    if (isManualOrder) {
      if (currentStep === 4) {
        setCurrentStep(3);
        return;
      }
      if (currentStep === 3) {
        // Manual order client cannot go back before step 3 (no pack/login change)
        return;
      }
    }
    // If logged in and on step 3, go back to step 1 (skip auth step)
    if (currentStep === 3 && isLoggedIn) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Use manual order pricing if available (with discount), otherwise use pack pricing
  const effectiveMonthlyPrice = (isManualOrder && manualOrderPricing?.finalPrice) ? manualOrderPricing.finalPrice : (selectedPack ? selectedPack.price : 0);
  const standardDeposit = (isManualOrder && manualOrderPricing?.deposit) ? manualOrderPricing.deposit : (selectedPack?.is_light_custom ? (selectedPack.caution ?? 0) : (selectedPack ? selectedPack.price : 0));
  // Protect+ : only for catalogue packs (not manual orders)
  const protectConfig = (!isManualOrder && selectedPack) ? getProtectPlus(selectedPack) : null;
  const protectAvailable = !!protectConfig;
  const protectActive = protectAvailable && protectPlus;
  const protectFee = protectActive ? protectConfig.fee : 0;
  const effectiveDeposit = protectActive ? protectConfig.reducedCaution : standardDeposit;
  const transportFees = (isManualOrder && manualOrderPricing?.transportFees) ? manualOrderPricing.transportFees : 0;
  const firstPayment = effectiveMonthlyPrice + effectiveDeposit + transportFees + protectFee;
  const monthlyPayment = effectiveMonthlyPrice + protectFee;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Full-screen redirect overlay: shown from click "Procéder au paiement" until Stripe loads */}
      {paymentRedirecting && (
        <div
          data-testid="payment-redirect-overlay"
          className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center px-6"
        >
          <div className="max-w-md w-full bg-slate-900 border border-[#DFFF00]/20 rounded-2xl p-8 shadow-2xl text-center">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-[#DFFF00]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#DFFF00] animate-spin" />
              <FileCheck className="w-9 h-9 text-[#DFFF00] absolute inset-0 m-auto" />
            </div>
            <h3 className="text-white text-xl font-bold mb-3">
              Certification de votre signature
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              <span className="text-[#DFFF00] font-semibold">PandaDoc</span> est en train de certifier juridiquement votre contrat.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed mb-6">
              Vous allez être redirigé vers notre partenaire de paiement sécurisé <span className="text-white font-semibold">Stripe</span> dans un instant.
            </p>
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Lock className="w-3 h-3" />
              <span>Transaction chiffrée de bout en bout</span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-black py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </button>
          <div className="flex items-center gap-4">
            <img src={LOGO_WHITE} alt="Phocea Rent" className="h-10 w-auto" />
            <h1 className="text-xl md:text-2xl font-bold text-white">Réservation de votre pack LED</h1>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    currentStep >= step.id
                      ? 'bg-[#DFFF00] text-black'
                      : 'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                  </div>
                  <span className={`hidden sm:block font-medium ${
                    currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-[#DFFF00]' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Pack Selection */}
        {currentStep === 1 && (
          <div>
            {selectedPack?.is_light_custom ? (
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#DFFF00] mb-2" data-testid="light-custom-summary">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Votre sélection sur-mesure</h2>
                  <span className="text-sm text-slate-500">{(selectedPack.items?.length || 0)} référence(s)</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {(selectedPack.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-700">{it.qty > 1 ? `${it.qty}× ` : ''}{it.name}</span>
                      <span className="font-semibold text-slate-900 whitespace-nowrap ml-4">{(Number(it.price) * (it.qty || 1)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/mois</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <span className="font-bold text-slate-900">{selectedPack.name}</span>
                  <span className="text-2xl font-bold text-slate-900">{Number(selectedPack.price).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €<span className="text-slate-500 text-base">/mois</span></span>
                </div>
              </div>
            ) : (
            <>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Choisissez votre pack</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {packs.map((pack) => {
                const packAvail = availability[String(pack.id)];
                const outOfStock = packAvail && packAvail.available === false;
                return (
                <div
                  key={pack.id}
                  onClick={() => {
                    if (!outOfStock) {
                      setSelectedPack(pack);
                      if (typeof window.fbq === 'function') window.fbq('track', 'ViewContent', {content_name: pack.name, content_ids: [pack.id], content_type: 'product', value: pack.price, currency: 'EUR'});
                    }
                  }}
                  className={`relative bg-white rounded-2xl p-6 transition-all ${
                    outOfStock
                      ? 'opacity-60 grayscale-[30%] cursor-not-allowed'
                      : 'cursor-pointer'
                  } ${
                    selectedPack?.id === pack.id && !outOfStock
                      ? 'ring-2 ring-[#DFFF00] shadow-lg'
                      : !outOfStock ? 'shadow hover:shadow-lg border border-slate-200' : 'shadow border border-slate-200'
                  }`}
                >
                  {selectedPack?.id === pack.id && !outOfStock && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-[#DFFF00] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-black" />
                    </div>
                  )}
                  {outOfStock && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full">Rupture de stock</span>
                    </div>
                  )}
                  <div className="flex gap-4">
                    <img src={pack.image} alt={pack.name} className="w-24 h-24 object-cover rounded-xl" />
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-900">
                        Pack <span className="text-[#DFFF00]" style={{textShadow: '0 0 8px rgba(223,255,0,0.5)'}}>{pack.size}</span>{pack.name.replace(`Pack ${pack.size}`, '')}
                      </h3>
                      <p className="text-[#9aae00] font-semibold">{pack.surface}</p>
                      <p className="text-sm text-slate-500 mt-1">{pack.description}</p>
                      <div className="mt-3">
                        <span className="text-2xl font-bold text-slate-900">{pack.price}€</span>
                        <span className="text-slate-500">/mois</span>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            </>
            )}

            {/* Date Selection Calendar */}
            {selectedPack && (
              <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#DFFF00] flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Date de début souhaitée <span className="text-red-500">*</span></h3>
                    <p className="text-sm text-slate-500">Réservation possible à partir de <strong>J+5</strong> (délai de préparation et livraison)</p>
                    {TEMP_BLOCK_UNTIL && (
                      <p className="text-sm text-amber-600 font-medium mt-1" data-testid="stock-shortage-notice">
                        En raison d'une forte demande, les premières dates disponibles sont à partir du <strong>19 juin 2026</strong>.
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Date Calendar - Always visible */}
                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Calendar */}
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => setDateRange({ from: date, to: null })}
                        locale={fr}
                        defaultMonth={(() => {
                          const d = getMinBookingDate();
                          return new Date(d.getFullYear(), d.getMonth(), 1);
                        })()}
                        fromDate={getMinBookingDate()}
                        disabled={(date) => {
                          const day = date.getDay();
                          const isWeekend = day === 0 || day === 6; // Dim/Sam
                          // Minimum réservation : J+5, ou plus tard si rupture de stock temporaire
                          const minDate = getMinBookingDate();
                          const target = new Date(date);
                          target.setHours(0, 0, 0, 0);
                          const isTooSoon = target < minDate;
                          return isWeekend || isTooSoon;
                        }}
                        className="rounded-xl border border-slate-200 bg-white shadow-sm"
                        classNames={{
                          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                          month: "space-y-4",
                          caption: "flex justify-center pt-1 relative items-center text-slate-900 font-semibold",
                          caption_label: "text-base font-semibold capitalize",
                          nav: "space-x-1 flex items-center",
                          nav_button: "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-slate-100 rounded-lg transition-all",
                          nav_button_previous: "absolute left-1",
                          nav_button_next: "absolute right-1",
                          table: "w-full border-collapse space-y-1",
                          head_row: "flex",
                          head_cell: "text-slate-500 rounded-md w-10 font-medium text-[0.8rem] uppercase",
                          row: "flex w-full mt-2",
                          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#DFFF00]/20 [&:has([aria-selected])]:rounded-lg",
                          day: "h-10 w-10 p-0 font-normal hover:bg-slate-100 rounded-lg transition-all aria-selected:opacity-100",
                          day_selected: "bg-[#DFFF00] text-black hover:bg-[#c8e600] hover:text-black focus:bg-[#DFFF00] focus:text-black font-semibold",
                          day_today: "bg-slate-100 text-slate-900 font-semibold",
                          day_outside: "text-slate-300 opacity-50",
                          day_disabled: "text-slate-300 opacity-30",
                          day_hidden: "invisible",
                        }}
                      />
                    </div>
                    
                    {/* Selected Date Info */}
                    <div className="flex-1 flex flex-col justify-center">
                      {dateRange.from ? (
                        <div className="bg-[#DFFF00]/10 rounded-xl p-5 border border-[#DFFF00]/30">
                          <p className="text-sm text-slate-600 mb-2">Date de début souhaitée</p>
                          <p className="text-xl font-bold text-slate-900 capitalize">
                            {format(dateRange.from, 'EEEE d MMMM yyyy', { locale: fr })}
                          </p>
                          <div className="mt-3 pt-3 border-t border-[#DFFF00]/30">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">Livraison estimée sous 48-72h</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setDateRange({ from: null, to: null })}
                            className="mt-3 text-xs text-slate-500 hover:text-red-500 transition-colors"
                          >
                            Effacer la date
                          </button>
                          <p className="text-xs text-slate-400 mt-3">Prévoir 1 à 2 jours de marge pour les délais de livraison par transporteur.</p>
                        </div>
                      ) : (
                        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 text-center">
                          <p className="text-slate-500 text-sm">Sélectionnez une date dans le calendrier</p>
                          <p className="text-xs text-slate-400 mt-1">Dates disponibles à partir de 5 jours</p>
                          <p className="text-xs text-slate-400 mt-2">Prévoir 1 à 2 jours de marge pour les délais de livraison par transporteur.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-4 italic">Date de fin : Vous vous engagez sur 1 mois minimum, ensuite résiliez à tout moment via votre compte client.</p>
              </div>
            )}

            {/* Return Information Block (collapsible to keep step 1 concise) */}
            {selectedPack && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowReturnInfo((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left"
                  data-testid="return-info-toggle"
                  aria-expanded={showReturnInfo}
                >
                  <span className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-800 text-sm">Comment se passe le retour du matériel ?</span>
                  </span>
                  <ChevronDown className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform ${showReturnInfo ? 'rotate-180' : ''}`} />
                </button>
                {showReturnInfo && (
                  <div className="px-4 pb-4 pl-12" data-testid="return-info-content">
                    <p className="text-blue-700 text-sm leading-relaxed">
                      Une fois votre location terminée, vous organisez le retour du matériel via la plateforme en ligne, au moins 3 jours avant la fin de votre mois de contrat. Les frais de retour sont à votre charge et vous sont facturés au moment de la demande de retour. Vous avez 2 jours maximum après la date de fin de contrat pour expédier votre marchandise. Une fois le matériel réceptionné et contrôlé, votre caution vous est intégralement restituée si aucun dommage n'est constaté.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Protect + choice at step 1 — price adapts to the chosen formula */}
            {protectAvailable && (
              <ProtectPlusSelector
                standardCaution={standardDeposit}
                monthlyPrice={effectiveMonthlyPrice}
                config={protectConfig}
                selected={protectPlus}
                onSelect={(v) => { setProtectPlus(v); if (!v) setProtectAccepted(false); }}
                accepted={protectAccepted}
                onAccept={setProtectAccepted}
              />
            )}

            {selectedPack && (
              <div className="mt-8 bg-[#DFFF00]/10 rounded-2xl p-6 border border-[#DFFF00]/30">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Récapitulatif des paiements</h3>
                
                {/* First Payment */}
                <div className="bg-white rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-[#9aae00]" />
                    <span className="font-semibold text-slate-900">Premier paiement (aujourd'hui)</span>
                  </div>
                  <div className="space-y-2 ml-7">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Location 1<sup>er</sup> mois</span>
                      <span className="font-semibold">{effectiveMonthlyPrice}€ TTC</span>
                    </div>
                    {transportFees > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Frais de livraison <span className="text-xs text-slate-400">(unique)</span></span>
                        <span className="font-semibold">{transportFees}€ TTC</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Caution (restituable)*</span>
                      <span className="font-semibold">{effectiveDeposit}€ TTC</span>
                    </div>
                    {protectActive && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Couverture Protect + <span className="text-xs text-slate-400">(1<sup>er</sup> mois)</span></span>
                        <span className="font-semibold">{protectFee}€ TTC</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between font-bold">
                        <span>Total premier paiement</span>
                        <span className="text-[#9aae00]">{firstPayment}€ TTC</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recurring Payment Info */}
                <div className="bg-white rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <RefreshCw className="w-5 h-5 text-[#9aae00]" />
                    <span className="font-semibold text-slate-900">Prélèvement mensuel (à partir du mois suivant)</span>
                  </div>
                  <div className="ml-7">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Location mensuelle</span>
                      <span className="font-bold text-[#9aae00]">{effectiveMonthlyPrice}€ TTC/mois</span>
                    </div>
                    {protectActive && (
                      <div className="flex justify-between mt-1">
                        <span className="text-slate-600">Couverture Protect +</span>
                        <span className="font-bold text-[#9aae00]">{protectFee}€ TTC/mois</span>
                      </div>
                    )}
                    {protectActive && (
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-semibold text-slate-900">Total mensuel</span>
                        <span className="font-bold text-[#9aae00]">{monthlyPayment}€ TTC/mois</span>
                      </div>
                    )}
                    {transportFees > 0 && (
                      <p className="text-xs text-emerald-700 mt-2 italic">
                        ✓ Pas de frais de livraison sur les mensualités suivantes — facturés une seule fois.
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      Prélèvement automatique chaque mois. Sans engagement, résiliable à tout moment.
                    </p>
                  </div>
                </div>

                {/* Date Summary if selected */}
                {dateRange.from && (
                  <div className="mt-4 bg-white rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-[#9aae00]" />
                      <span className="text-slate-600">Date de début souhaitée :</span>
                      <span className="font-semibold text-slate-900 capitalize">
                        {format(dateRange.from, 'd MMMM yyyy', { locale: fr })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Deposit Note */}
                <p className="mt-4 text-xs text-slate-500 italic">
                  *Restituable entièrement si aucun dommage n'est constaté après contrôle du matériel.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Login / Create Account */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Connexion ou création de compte</h2>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-lg mx-auto">
              {/* Google Login */}
              <Button
                onClick={handleGoogleLogin}
                className="w-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 font-semibold py-6 rounded-xl flex items-center justify-center gap-3 shadow-sm"
                data-testid="reservation-google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-sm text-slate-400">ou par email</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              {/* Auth Mode Tabs */}
              <div className="flex mb-6 bg-slate-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setAuthMode('login')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    authMode === 'login' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Se connecter
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMode('register')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    authMode === 'register' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Créer un compte
                </button>
              </div>

              {/* Login Form */}
              {authMode === 'login' && (
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="auth-email" className="text-slate-700">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-email"
                        name="email"
                        type="email"
                        value={authData.email}
                        onChange={handleAuthInputChange}
                        placeholder="votre@email.com"
                        className="pl-10 h-12"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="auth-password" className="text-slate-700">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={authData.password}
                        onChange={handleAuthInputChange}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-sm text-[#9aae00] hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold py-6 rounded-xl"
                  >
                    {authLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <LogIn className="w-5 h-5 mr-2" />
                        Se connecter
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Register Form */}
              {authMode === 'register' && (
                <form onSubmit={handleEmailRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="auth-name" className="text-slate-700">Nom complet</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-name"
                        name="name"
                        type="text"
                        value={authData.name}
                        onChange={handleAuthInputChange}
                        placeholder="Jean Dupont"
                        className="pl-10 h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="auth-reg-email" className="text-slate-700">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-reg-email"
                        name="email"
                        type="email"
                        value={authData.email}
                        onChange={handleAuthInputChange}
                        placeholder="votre@email.com"
                        className="pl-10 h-12"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="auth-reg-password" className="text-slate-700">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-reg-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={authData.password}
                        onChange={handleAuthInputChange}
                        placeholder="Min. 8 caractères"
                        className="pl-10 pr-10 h-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="auth-confirm-password" className="text-slate-700">Confirmer le mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="auth-confirm-password"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={authData.confirmPassword}
                        onChange={handleAuthInputChange}
                        placeholder="••••••••"
                        className="pl-10 h-12"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold py-6 rounded-xl"
                  >
                    {authLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <User className="w-5 h-5 mr-2" />
                        Créer mon compte
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Benefits */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-3">En créant un compte, vous bénéficiez de :</p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#9aae00]" />
                    Suivi de vos locations en temps réel
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#9aae00]" />
                    Accès à vos factures et contrats
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#9aae00]" />
                    Historique de vos réservations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Customer Information */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Vos informations</h2>

            {/* Manual Order — info box from commercial */}
            {isManualOrder && (
              <div className="bg-gradient-to-r from-[#DFFF00]/15 to-[#9aae00]/10 border border-[#DFFF00] rounded-2xl p-5 mb-6" data-testid="manual-order-banner">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#DFFF00] flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-slate-900" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-900">Commande pré-remplie par votre commercial</p>
                    <p className="text-sm text-slate-600 mt-0.5">Vérifiez vos informations, ajoutez votre RC professionnelle et validez pour signer le contrat.</p>
                  </div>
                </div>

                {(manualOrderPricing?.discountValue > 0 || (manualOrderPricing?.transportFees ?? 0) > 0) && (
                  <div className="mt-4 pt-4 border-t border-[#DFFF00]/40 grid sm:grid-cols-2 gap-3">
                    {manualOrderPricing?.discountValue > 0 && (
                      <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-emerald-200" data-testid="discount-badge">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">%</div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Remise commerciale</p>
                          <p className="font-bold text-emerald-700">
                            -{manualOrderPricing.discountValue}{manualOrderPricing.discountType === 'percentage' ? '%' : '€'} appliquée
                          </p>
                        </div>
                      </div>
                    )}
                    {(manualOrderPricing?.transportFees ?? 0) > 0 && (
                      <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-amber-200">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                          <Package className="w-4 h-4 text-amber-700" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Frais de livraison</p>
                          <p className="font-bold text-amber-700">{manualOrderPricing.transportFees}€ <span className="text-xs font-medium text-slate-500">(1<sup>er</sup> paiement uniquement)</span></p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Connected User Info */}
            {isLoggedIn && googleUser && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">Connecté</p>
                  <p className="text-sm text-green-600">{googleUser.email}</p>
                </div>
                <div className="text-xs text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  Espace client activé
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName">Prénom *</Label>
                  <div className="relative">
                    <Input
                      id="firstName"
                      name="firstName"
                      data-testid="input-firstname"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Jean"
                      className={`mt-2 h-12 ${isGoogleUser ? 'bg-slate-50 pr-10' : ''}`}
                      readOnly={isGoogleUser}
                    />
                    {isGoogleUser && (
                      <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="lastName">Nom *</Label>
                  <div className="relative">
                    <Input
                      id="lastName"
                      name="lastName"
                      data-testid="input-lastname"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Dupont"
                      className={`mt-2 h-12 ${isGoogleUser ? 'bg-slate-50 pr-10' : ''}`}
                      readOnly={isGoogleUser}
                    />
                    {isGoogleUser && (
                      <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      data-testid="input-email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jean@example.com"
                      className={`mt-2 h-12 ${isGoogleUser ? 'bg-slate-50 pr-10' : ''}`}
                      readOnly={isGoogleUser}
                    />
                    {isGoogleUser && (
                      <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="06 12 34 56 78"
                    className="mt-2 h-12"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="company">Entreprise *</Label>
                  <Input
                    id="company"
                    name="company"
                    data-testid="input-company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Nom de votre entreprise"
                    className="mt-2 h-12"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="siren">N° SIREN *</Label>
                  <Input
                    id="siren"
                    name="siren"
                    data-testid="input-siren"
                    value={formData.siren}
                    onChange={handleInputChange}
                    placeholder="123 456 789"
                    className="mt-2 h-12"
                    maxLength={11}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">9 chiffres identifiant votre entreprise</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="activityType">Type d'activité *</Label>
                  <select
                    id="activityType"
                    name="activityType"
                    data-testid="select-activity-type"
                    value={formData.activityType}
                    onChange={handleInputChange}
                    className="mt-2 h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    required
                  >
                    <option value="">Sélectionnez votre activité</option>
                    <option value="prestataire_evenementiel">Prestataire événementiel</option>
                    <option value="dj">DJ</option>
                    <option value="etablissement">Établissement</option>
                    <option value="particulier">Particulier</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Adresse de livraison *</Label>
                  <Input
                    id="address"
                    name="address"
                    data-testid="input-address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Rue Example"
                    className="mt-2 h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    name="city"
                    data-testid="input-city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Marseille"
                    className="mt-2 h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode">Code postal *</Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    data-testid="input-postalcode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="13000"
                    className="mt-2 h-12"
                  />
                </div>
              </div>

              {/* RC Upload — masqué si Protect + actif (la RC n'est plus demandée) */}
              {protectActive ? (
                <div className="mt-8 border-t pt-6" data-testid="rc-pro-not-required-protect">
                  <div className="flex items-start gap-3 rounded-xl bg-[#DFFF00]/10 border border-[#9aae00]/30 p-4">
                    <Shield className="w-6 h-6 text-[#9aae00] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-slate-900">Attestation RC Professionnelle non requise</h3>
                      <p className="text-sm text-slate-600 mt-0.5">
                        Avec la couverture <strong>Protect +</strong>, vous n'avez pas besoin de fournir d'attestation
                        de responsabilité civile professionnelle pour cette location.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="mt-8 border-t pt-6">
                <div className="flex items-start gap-3 mb-4">
                  <Shield className="w-6 h-6 text-[#9aae00] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-slate-900">Attestation RC Professionnelle *</h3>
                    <p className="text-sm text-slate-500">Document obligatoire pour la location de matériel</p>
                  </div>
                </div>
                
                <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  rcFile ? 'border-[#DFFF00] bg-[#DFFF00]/5' : 'border-slate-300 hover:border-[#DFFF00]'
                }`}>
                  {rcFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileCheck className="w-8 h-8 text-[#9aae00]" />
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{rcFile.name}</p>
                        <p className="text-sm text-slate-500">{(rcFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        onClick={() => setRcFile(null)}
                        className="ml-4 text-sm text-red-500 hover:underline"
                      >
                        Supprimer
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Cliquez pour télécharger votre RC</p>
                      <p className="text-sm text-slate-400 mt-1">PDF, JPG ou PNG (max 10MB)</p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
              )}

              {/* Payment Summary */}
              <div className="mt-8 bg-black rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-4">Récapitulatif des paiements</h3>
                
                <div className="flex gap-4 mb-4 pb-4 border-b border-slate-700">
                  <img src={selectedPack?.image} alt={selectedPack?.name} className="w-16 h-16 object-cover rounded-lg" />
                  <div>
                    <p className="font-semibold">{selectedPack?.name}</p>
                    <p className="text-[#DFFF00]">{selectedPack?.surface}</p>
                  </div>
                </div>

                {/* First Payment */}
                <div className="mb-4 pb-4 border-b border-slate-700">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-[#DFFF00]" />
                    <span className="text-sm font-medium">Premier paiement</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Location 1<sup>er</sup> mois</span>
                      <span>{effectiveMonthlyPrice}€</span>
                    </div>
                    {transportFees > 0 && (
                      <div className="flex justify-between text-slate-300">
                        <span>Frais de livraison <span className="text-xs text-slate-400">(unique)</span></span>
                        <span>{transportFees}€</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-300">
                      <span>Caution*</span>
                      <span>{effectiveDeposit}€</span>
                    </div>
                  </div>
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-slate-600">
                    <span>Total aujourd'hui</span>
                    <span className="text-[#DFFF00]">{firstPayment}€ TTC</span>
                  </div>
                </div>

                {/* Recurring */}
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-[#DFFF00]" />
                  <span className="text-sm font-medium">Puis chaque mois</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Prélèvement mensuel</span>
                  <span className="font-bold text-[#DFFF00]">{monthlyPayment}€ TTC</span>
                </div>
                {transportFees > 0 && (
                  <p className="text-xs text-emerald-400 mt-2 italic">
                    ✓ Frais de livraison facturés une seule fois (pas sur les mensualités suivantes).
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Sans engagement • Résiliable à tout moment
                </p>
                <p className="text-xs text-slate-400 mt-2 italic">
                  *Restituable entièrement si aucun dommage n'est constaté après contrôle du matériel.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Contract & Signature */}
        {currentStep === 4 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Contrat de location</h2>
            
            {/* Manual order info banner */}
            {isManualOrder && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="manual-order-banner">
                <div className="flex items-center gap-2 text-amber-800 mb-2">
                  <Shield className="w-5 h-5" />
                  <span className="font-bold">Commande préparée par votre commercial</span>
                </div>
                <p className="text-sm text-amber-700">
                  Votre commercial <strong>{formData.company ? `pour ${formData.company}` : ''}</strong> a pré-rempli votre commande. 
                  Veuillez vérifier les informations ci-dessous, accepter les conditions générales, puis procéder au paiement.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-amber-800">
                  <p><strong>Pack :</strong> {selectedPack?.name}</p>
                  <p><strong>Prix :</strong> {effectiveMonthlyPrice}€/mois{manualOrderPricing?.discountValue > 0 ? ` (remise -${manualOrderPricing.discountType === 'percentage' ? `${manualOrderPricing.discountValue}%` : `${manualOrderPricing.discountValue}€`})` : ''}</p>
                  <p><strong>Client :</strong> {formData.firstName} {formData.lastName}</p>
                  <p><strong>Email :</strong> {formData.email}</p>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Contract Display */}
              <div className="p-6 border-b">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-6 h-6 text-[#9aae00]" />
                  <h3 className="font-bold text-slate-900">Conditions Générales de Location</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Veuillez lire attentivement le contrat ci-dessous avant de signer.
                </p>
                
                {/* Scrollable Contract */}
                <div 
                  className="h-96 overflow-y-auto border rounded-xl p-6 bg-slate-50"
                  data-testid="contract-container"
                >
                  <ContractDisplay 
                    customerData={formData}
                    packData={selectedPack}
                    deposit={effectiveDeposit}
                    protectActive={protectActive}
                    protectFee={protectFee}
                  />
                </div>
              </div>

              {/* Protect + avenant recap (only if subscribed at step 1) */}
              {protectActive && (
                <div className="p-6 border-b bg-[#DFFF00]/5" data-testid="step4-protect-avenant">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-6 h-6 text-[#9aae00]" />
                    <h3 className="font-bold text-slate-900">Avenant — Couverture Protect +</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    Vous avez choisi la couverture Protect +. Voici les conditions appliquées à votre contrat :
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between bg-white border rounded-lg px-4 py-3">
                      <span className="text-slate-600">Forfait Protect +</span>
                      <span className="font-bold text-slate-900">{protectFee}€ TTC/mois</span>
                    </div>
                    <div className="flex justify-between bg-white border rounded-lg px-4 py-3">
                      <span className="text-slate-600">Caution réduite</span>
                      <span className="font-bold text-[#9aae00]">{effectiveDeposit}€ <span className="text-xs text-slate-400 line-through ml-1">{standardDeposit}€</span></span>
                    </div>
                    <div className="flex justify-between bg-white border rounded-lg px-4 py-3">
                      <span className="text-slate-600">Franchise casse / dégâts</span>
                      <span className="font-semibold text-slate-900">{PROTECT_PLUS_FRANCHISE.damage}€ / sinistre</span>
                    </div>
                    <div className="flex justify-between bg-white border rounded-lg px-4 py-3">
                      <span className="text-slate-600">Franchise vol</span>
                      <span className="font-semibold text-slate-900">{PROTECT_PLUS_FRANCHISE.theft}€ / sinistre</span>
                    </div>
                  </div>
                  <a
                    href="/avenant-protect-plus.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-[#9aae00] hover:underline text-sm font-medium"
                    data-testid="step4-avenant-pdf-link"
                  >
                    <FileText className="w-4 h-4" /> Consulter l'avenant Protect + complet (PDF)
                  </a>
                </div>
              )}

              {/* Signature Section */}
              <div className="p-6 bg-slate-50">
                <div className="flex items-center gap-3 mb-4">
                  <PenTool className="w-6 h-6 text-[#9aae00]" />
                  <h3 className="font-bold text-slate-900">Signature du contrat</h3>
                </div>

                {/* Signed Banner */}
                {(pandadocSigned || signatureData) && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="signature-success-banner">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">
                        {pandadocSigned ? 'Contrat signé et certifié par PandaDoc' : 'Signature enregistrée'}
                      </span>
                    </div>
                    {signatureData && !pandadocSigned && (
                      <div className="mt-3 flex items-center gap-4">
                        <span className="text-sm text-green-600">Votre signature :</span>
                        <img src={signatureData} alt="Signature" className="h-12 border-b border-slate-300" />
                      </div>
                    )}
                    <p className="text-sm text-green-600 mt-2">
                      Vous pouvez maintenant procéder au paiement.
                    </p>
                  </div>
                )}

                {/* Signing Flow - only show if not yet signed */}
                {!pandadocSigned && !signatureData && (
                  <>
                    {/* CGV Checkbox */}
                    <div className="mb-5 flex items-start gap-3">
                      <Checkbox
                        id="contract-accept"
                        checked={contractAccepted}
                        onCheckedChange={setContractAccepted}
                        className="mt-1 data-[state=checked]:bg-[#9aae00] data-[state=checked]:border-[#9aae00]"
                        data-testid="contract-accept-checkbox"
                      />
                      <label 
                        htmlFor="contract-accept" 
                        className="text-sm text-slate-700 cursor-pointer"
                      >
                        Je reconnais avoir lu et accepté les <strong>Conditions Générales de Location</strong>. 
                        Je certifie que les informations fournies sont exactes et je m'engage à respecter 
                        les termes du contrat.
                      </label>
                    </div>

                    {contractAccepted && (
                      <div data-testid="signature-area">
                        <p className="text-sm text-slate-600 mb-3 font-medium">
                          Signez ci-dessous avec votre souris ou votre doigt :
                        </p>
                        <div ref={sigContainerRef} className="border-2 border-slate-300 rounded-xl bg-white overflow-hidden relative" style={{ touchAction: 'none' }}>
                          <SignatureCanvas
                            ref={sigCanvasRef}
                            penColor="#000"
                            canvasProps={{
                              width: canvasWidth,
                              height: 200,
                              className: 'signature-canvas',
                              style: { width: `${canvasWidth}px`, height: '200px' }
                            }}
                          />
                          <div className="absolute bottom-3 left-4 right-4 border-b border-dashed border-slate-300" />
                          <span className="absolute bottom-1 left-4 text-xs text-slate-300">Signature</span>
                        </div>
                        <div className="flex gap-3 mt-3">
                          <button
                            onClick={clearSignature}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-100 transition"
                            data-testid="clear-signature-btn"
                          >
                            <Eraser className="w-4 h-4" />
                            Effacer
                          </button>
                          <button
                            onClick={saveSignature}
                            disabled={pandadocLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white bg-[#9aae00] hover:bg-[#8a9e00] rounded-lg font-semibold text-sm transition disabled:opacity-50"
                            data-testid="validate-signature-btn"
                          >
                            {pandadocLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Certification en cours...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Valider ma signature
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-slate-400 text-center mt-2">
                          Votre signature sera certifiée par PandaDoc — valeur juridique
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mt-6 bg-black rounded-2xl p-6 text-white">
              <h3 className="font-bold text-lg mb-4">Récapitulatif</h3>
              <div className="flex gap-4 mb-4 pb-4 border-b border-slate-700">
                <img src={selectedPack?.image} alt={selectedPack?.name} className="w-16 h-16 object-cover rounded-lg" />
                <div>
                  <p className="font-semibold">{selectedPack?.name}</p>
                  <p className="text-[#DFFF00]">{selectedPack?.surface}</p>
                </div>
              </div>
              {protectActive && (
                <div className="flex justify-between mb-2 text-sm" data-testid="recap-protect-line">
                  <span className="text-[#DFFF00]">Couverture Protect + (incluse chaque mois)</span>
                  <span className="font-semibold text-[#DFFF00]">{protectFee}€/mois</span>
                </div>
              )}
              <div className="flex justify-between mb-2">
                <span className="text-slate-300">Premier paiement (Location + Caution{protectActive ? ' + Protect +' : ''})</span>
                <span className="font-bold text-[#DFFF00]">{firstPayment}€ TTC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">Puis chaque mois</span>
                <span className="font-bold text-[#DFFF00]">{monthlyPayment}€ TTC</span>
              </div>

              {/* Payment method selector (CB / SEPA) */}
              {(pandadocSigned || signatureData) && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <PaymentMethodSelector
                    method={paymentMethod}
                    onChange={(m) => {
                      setPaymentMethod(m);
                      if (m === 'sepa') {
                        // Warmup Stripe.js the moment user picks SEPA → modal opens faster on submit.
                        import('../lib/stripe').then(mod => mod.getStripe());
                      }
                    }}
                    total={firstPayment + (transportFees || 0)}
                  />
                  {paymentMethod === 'sepa' && (
                    <div className="mt-3 text-xs text-yellow-300/90 bg-yellow-400/5 border border-yellow-400/20 rounded-md p-3" data-testid="sepa-info-banner">
                      Vous serez invité à saisir votre IBAN et signer le mandat à l'étape suivante.
                      Le premier prélèvement n'aura lieu qu'après <strong>validation par notre équipe sous 48h ouvrées</strong>.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SEPA IBAN modal — opens INSTANTLY with a spinner, then renders the form once client_secret arrives */}
            {sepaFormOpen && (
              <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="sepa-modal">
                <div className="bg-zinc-900 border border-yellow-400/30 rounded-2xl p-6 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Signature du mandat SEPA</h3>
                    <button
                      onClick={() => setSepaFormOpen(false)}
                      className="text-white/40 hover:text-white text-2xl leading-none"
                      data-testid="sepa-modal-close"
                    >
                      ×
                    </button>
                  </div>
                  {sepaClientSecret ? (
                    <SEPAPaymentForm
                      clientSecret={sepaClientSecret}
                      customerName={`${formData.firstName} ${formData.lastName}`.trim()}
                      customerEmail={formData.email}
                      firstChargeAmount={sepaCheckout?.first_charge_amount || 0}
                      signatureImage={signatureData}
                      onMandateSigned={onSEPAMandateSigned}
                      onCancel={() => setSepaFormOpen(false)}
                    />
                  ) : (
                    <div className="py-12 flex flex-col items-center justify-center gap-3 text-white/70" data-testid="sepa-modal-loader">
                      <div className="w-10 h-10 rounded-full border-2 border-yellow-400/30 border-t-yellow-400 animate-spin" />
                      <div className="text-sm">Préparation du mandat sécurisé…</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Processing Payment */}
        {currentStep === 5 && isProcessing && (
          <div className="text-center py-20">
            <Loader2 className="w-16 h-16 text-[#DFFF00] mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Vérification du paiement en cours...</h2>
            <p className="text-slate-600">Veuillez patienter pendant que nous confirmons votre paiement et activons votre abonnement.</p>
          </div>
        )}

        {/* Step 6: Confirmation - En attente de validation */}
        {currentStep === 6 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <Clock className="w-12 h-12 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Paiement reçu avec succès !</h2>
            <p className="text-lg text-slate-600 max-w-lg mx-auto mb-4">
              Merci pour votre demande de location. Votre paiement a bien été enregistré.
            </p>
            
            {/* Banner validation en cours */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 max-w-lg mx-auto mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                <span className="text-amber-700 font-bold text-lg">En attente de validation</span>
              </div>
              <p className="text-amber-700 text-sm">
                Notre équipe va vérifier votre dossier (paiement, attestation RC Pro, informations client) 
                et vous confirmera votre réservation sous <strong>24 à 48 heures</strong>.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 max-w-lg mx-auto shadow-lg text-left">
              <h3 className="font-bold text-lg text-slate-900 mb-4">Récapitulatif de votre demande</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-500">Pack</span>
                  <span className="font-semibold">
                    {paymentDetails?.packName || selectedPack?.name} ({paymentDetails?.packSurface || selectedPack?.surface})
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-500">Client</span>
                  <span className="font-semibold">
                    {paymentDetails?.customer?.firstName || formData.firstName} {paymentDetails?.customer?.lastName || formData.lastName}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-500">Livraison</span>
                  <span className="font-semibold">
                    {paymentDetails?.customer?.city || formData.city}, {paymentDetails?.customer?.postalCode || formData.postalCode}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-500">Payé aujourd'hui</span>
                  <span className="font-bold text-green-600">
                    {(paymentDetails?.monthlyPrice || selectedPack?.price) + (paymentDetails?.caution || selectedPack?.price)}€ TTC ✓
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-500">Prélèvement mensuel</span>
                  <span className="font-bold text-[#9aae00]">
                    {paymentDetails?.monthlyPrice || selectedPack?.price}€ TTC/mois
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Contrat</span>
                  <span className="font-semibold text-green-600 flex items-center gap-1">
                    <FileCheck className="w-4 h-4" />
                    Signé électroniquement
                  </span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-blue-800 text-sm font-medium mb-2">📧 Que se passe-t-il ensuite ?</p>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Vous recevrez un email de confirmation de réception</li>
                  <li>• Notre équipe analyse votre dossier sous 24-48h</li>
                  <li>• Une fois validé, vous recevrez l'email de confirmation définitive</li>
                </ul>
              </div>

              {/* Important Tip */}
              <div className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800 text-sm mb-1">ASTUCE IMPORTANTE</p>
                    <p className="text-amber-700 text-sm">
                      Pensez à conserver vos palettes et sangles à réception de la marchandise pour réexpédier dans les mêmes conditions d'emballage. Il faut également protéger la marchandise avec un film de protection.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500">
                  💡 En cas de refus de votre demande, votre paiement et votre caution de {paymentDetails?.caution || selectedPack?.price}€ 
                  seront intégralement remboursés sous 2 à 5 jours ouvrés.
                </p>
              </div>
            </div>

            <div className="mt-8 flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8 py-3 rounded-full"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {currentStep < 5 && currentStep !== 2 && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              onClick={prevStep}
              variant="outline"
              disabled={currentStep === 1}
              className="px-6 py-3 rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            <Button
              onClick={nextStep}
              disabled={isProcessing || (currentStep === 1 && (!selectedPack || !dateRange.from)) || (currentStep === 4 && !(pandadocSigned || signatureData))}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8 py-3 rounded-full disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  {currentStep === 4 ? 'Procéder au paiement' : 'Continuer'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Navigation for Step 2 (Auth) - only back button */}
        {currentStep === 2 && (
          <div className="flex justify-start mt-8 pt-6 border-t">
            <Button
              onClick={prevStep}
              variant="outline"
              className="px-6 py-3 rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationPage;
